import Vue from 'vue';
import VueI18n from 'vue-i18n';
import axios from 'axios';
import VueMq from 'vue-mq';
import VueMoment from 'vue-moment';
import moment from 'moment-timezone';
/** required for IE9, IE10, and IE11 **/
import 'core-js';
import svg4everybody from 'svg4everybody';
import bowser from 'bowser';
import customDirectives from './directives';

import '@/scss/bootstrap.scss';
import '@/scss/global.scss';
import '@/components/global';

Vue.prototype.$routingBus = new Vue(); // Global routing bus

Vue.config.productionTip = false;

Vue.use(VueI18n);
Vue.use(VueMq, {
    breakpoints: {
        sm: 768,
        md: 992,
        lg: 1200,
        xl: Infinity
    }
});

Vue.use(customDirectives);

const browser = bowser.getParser(window.navigator.userAgent);
// This header goes on all axios qv4api requests
// Cache-Control is not working for IE so we have to use Pragma :(
axios.interceptors.request.use(config => {
    if (config.url.includes(process.env.VUE_APP_QV4_API)) {
        config.headers.Pragma = 'no-cache';
        config.headers['app-version'] = 3;
    }

    return config;
});

const requiredPages = require.context(
    // The relative path of the components folder
    './pages',
    // Whether or not to look in subfolders
    true,
    // The regular expression used to match all component filenames
    /[a-z]\w+\.vue$/
);

const requiredComponents = require.context(
    // The relative path of the components folder
    './components/global',
    // Whether or not to look in subfolders
    true,
    // The regular expression used to match all component filenames
    /[a-z]\w+\.vue$/
);

const initApp = async (selector, pageConfig, options) => {
    options = options || {};
    // True when PHP/shell passed locale into createPage (user account language), not only browser default.
    const localeInjectedFromShell =
        typeof options.locale !== 'undefined' &&
        options.locale !== null &&
        String(options.locale).trim() !== '';

    // Get all environment variables that we need.
    let qv3translations = process.env.VUE_APP_QV3_TRANSLATIONS,
        userLanguage =
            window.navigator.userLanguage || window.navigator.language,
        browserLocale = userLanguage ? userLanguage.substring(0, 2) : null,
        messages = {},
        // Function to get application information by default.
        getApplications = () => {
            let storedAppsData = window.localStorage.getItem('apps');

            if (storedAppsData) {
                return;
            }

            return axios.get(
                `${
                    process.env.VUE_APP_QV4_API
                }/applications?fields=id,translated`,
                {
                    headers: {
                        'x-api-key': options.token
                    }
                }
            );
        },
        getApplicationsLocalizationExceptions = () => {
            let storedAppsData = window.localStorage.getItem('apps');

            if (storedAppsData) {
                return;
            }

            return axios.get(
                `${
                    process.env.VUE_APP_QV4_API
                }/applications/localization-exceptions`,
                {
                    headers: {
                        'x-api-key': options.token
                    }
                }
            );
        },
        getLanguages = () => {
            let storedLanguages = window.localStorage.getItem('languages');

            if (storedLanguages) {
                return;
            }

            return axios.get(
                `${
                    process.env.VUE_APP_QV4_API
                }/languages?fields=codeGoogle,isTranslated,translated`,
                {
                    headers: {
                        'x-api-key': options.token
                    }
                }
            );
        },
        // Function to get english translations by default.
        getEnglishTranslations = () => {
            let storedEnglish = window.localStorage.getItem('en-trans');

            if (storedEnglish && storedEnglish !== 'undefined') {
                return JSON.parse(storedEnglish);
            }

            return axios.get(`${qv3translations}/en`);
        },
        // Function to get browser (or shell-injected account) language translations, if not english.
        // Login shells do not include header_v4's window.localizedLanguages; still load glossary when
        // locale is injected from PHP, or when localizedLanguages is missing, or when browser lang is listed.
        getBrowserTranslations = () => {
            if (!browserLocale || browserLocale === 'en') {
                return;
            }
            const inGlobalWhitelist =
                Array.isArray(window.localizedLanguages) &&
                window.localizedLanguages.includes(browserLocale);
            const whitelistMissing =
                typeof window.localizedLanguages === 'undefined' ||
                window.localizedLanguages === null;
            const shouldFetch =
                localeInjectedFromShell ||
                whitelistMissing ||
                inGlobalWhitelist;

            if (!shouldFetch) {
                return;
            }

            browserLocale =
                browserLocale === 'zh' ? 'zh-CN' : browserLocale;

            let storedTranslations = window.localStorage.getItem(
                browserLocale + '-trans'
            );

            if (storedTranslations && storedTranslations !== 'undefined') {
                return JSON.parse(storedTranslations);
            }

            return axios
                .get(`${qv3translations}/${browserLocale}`)
                .then(data => {
                    return data;
                })
                .catch(() => {
                    return false;
                });
        };
    // Function to get system messages.
    // getSystemMessages = () => {
    //     return axios.get(
    //         `${process.env.VUE_APP_QV3_API}/systemmessages/unread?app_id=${
    //             options.appId
    //         }&platform=desktop`,
    //         {
    //             headers: {
    //                 'X-Api-Key': options.token
    //             }
    //         }
    //     );
    // };

    options.isMobile = window.location.pathname.match(/mobile/) !== null;

    if (options.locale) {
        browserLocale = options.locale.substring(0, 2);
        // all of the locales in moment are the first 2 characters except chinese
        moment.locale(browserLocale === 'zh' ? 'zh-cn' : browserLocale);
    }

    if (options.propertyTimezone) {
        moment.tz.setDefault(options.propertyTimezone);
    }

    if (browser) {
        options.browser = browser.parsedResult.browser.name
            .toLowerCase()
            .replace(/\s/g, '-');
    }

    Vue.use(VueMoment, {
        moment
    });

    let data = {
        hash: process.env.VUE_APP_QV4_API_HASH,
        name: 'WEB'
    };

    let storedAuth = window.localStorage.getItem('authenticated');
    let qv4token = '';

    if (storedAuth && storedAuth !== 'undefined') {
        const qv4auth = JSON.parse(storedAuth).v4;

        if (qv4auth && qv4auth.token) {
            qv4token = qv4auth.token;
            options.token = qv4auth.token;
        }
    }

    if (!qv4token) {
        await axios
            .post(`${process.env.VUE_APP_QV4_API}/environments/login`, data)
            .then(response => {
                options.token = response.data.token;
            })
            .catch(() => {});
    }

    // Perform multiple axios requests at once, and wait till all are done.
    axios
        .all([
            getApplications(),
            getApplicationsLocalizationExceptions(),
            getLanguages(),
            getEnglishTranslations(),
            getBrowserTranslations()
        ])

        // Get the localized translations from s3 based on browser language.
        // Add translations for browser language to the messages object.
        // If the v3token is not stored in localStorage, get it, else use what's in local storage.
        .then(
            axios.spread(
                async (
                    applications,
                    localizationExceptions,
                    languages,
                    englishTranslations,
                    browserTranslations
                ) => {
                    let translationExpire = 0,
                        timestamp = 0;

                    if (window.localStorage.getItem('trans-expire')) {
                        translationExpire = JSON.parse(
                            window.localStorage.getItem('trans-expire')
                        );
                    }
                    timestamp = moment.utc().valueOf();

                    if (
                        !window.localStorage.getItem('en-trans') ||
                        !window.localStorage.getItem('trans-expire') ||
                        translationExpire < timestamp
                    ) {
                        localStorage.removeItem('en-trans');
                        window.localStorage.setItem(
                            'en-trans',
                            JSON.stringify(englishTranslations)
                        );
                        localStorage.removeItem('trans-expire');
                        window.localStorage.setItem(
                            'trans-expire',
                            moment
                                .utc()
                                .add(1, 'hours')
                                .valueOf()
                        );
                    }

                    // Get english translations no matter what since that's our fallback.
                    messages['en'] = JSON.parse(
                        JSON.stringify(englishTranslations.data)
                            .replace(/\{\{/g, '{')
                            .replace(/\}\}/g, '}')
                    );

                    // If the browser language is different than our english default.
                    if (browserTranslations) {
                        if (
                            !window.localStorage.getItem(
                                browserLocale + '-trans'
                            ) ||
                            !window.localStorage.getItem('trans-expire') ||
                            translationExpire < timestamp
                        ) {
                            window.localStorage.removeItem(
                                browserLocale + '-trans'
                            );
                            window.localStorage.setItem(
                                browserLocale + '-trans',
                                JSON.stringify(browserTranslations)
                            );
                        }

                        messages[browserLocale] = JSON.parse(
                            JSON.stringify(browserTranslations.data)
                                .replace(/\{\{/g, '{')
                                .replace(/\}\}/g, '}')
                        );
                    }

                    let authenticated = JSON.parse(
                        window.localStorage.getItem('authenticated')
                    );

                    // Handle applications here.
                    if (applications && !window.localStorage.getItem('apps')) {
                        let appData = applications.data;
                        options.apps = [];

                        // Loop through apps -- apply exceptions array, then add app to list.
                        appData.forEach(appInfo => {
                            appInfo.exceptions = [];
                            options.apps[appInfo.id] = appInfo;
                        });

                        // If exceptions, loop through and add to specific app's exceptions.
                        if (localizationExceptions) {
                            let exceptions = localizationExceptions.data;

                            exceptions.forEach(exception => {
                                options.apps[
                                    parseInt(exception.applicationId)
                                ].exceptions.push(exception.url);
                            });
                        }

                        window.localStorage.setItem(
                            'apps',
                            JSON.stringify(options.apps)
                        );
                    } else {
                        options.apps = JSON.parse(
                            window.localStorage.getItem('apps')
                        );
                    }

                    if (
                        languages &&
                        !window.localStorage.getItem('languages')
                    ) {
                        let languageData = languages.data.data;
                        options.languages = {};

                        languageData.forEach(languageInfo => {
                            options.languages[
                                languageInfo.codeGoogle
                            ] = languageInfo;
                        });

                        window.localStorage.setItem(
                            'languages',
                            JSON.stringify(options.languages)
                        );
                    } else {
                        options.languages = JSON.parse(
                            window.localStorage.getItem('languages')
                        );
                    }

                    if (
                        typeof options.token === 'undefined' ||
                        !options.token
                    ) {
                        options.token = authenticated
                            ? authenticated.v4.token
                            : null;
                    }

                    // if (options.token && typeof options.appId !== 'undefined') {
                    //     await getSystemMessages(options.token, options.appId)
                    //         .then(systemMessages => {
                    //             // Now that we have v3token and appId, get System Messages.
                    //             if (
                    //                 systemMessages &&
                    //                 systemMessages.data.data.length
                    //             ) {
                    //                 let messages = systemMessages.data.data,
                    //                     messagesCount = messages.length;

                    //                 // If there is more than 1 system message to be displayed.
                    //                 if (messagesCount > 1) {
                    //                     let endingFirst = new Date(
                    //                             messages[0].end_date
                    //                         ),
                    //                         index = 0;

                    //                     // Loop through and grab the appropriate message to display.
                    //                     for (
                    //                         let i = 1;
                    //                         i < messagesCount;
                    //                         i++
                    //                     ) {
                    //                         let currentEndDate = new Date(
                    //                             messages[i].end_date
                    //                         );

                    //                         if (currentEndDate < endingFirst) {
                    //                             endingFirst = currentEndDate;
                    //                             index = i;
                    //                         }
                    //                     }

                    //                     // Apply most appropriate message.
                    //                     options.systemMessage = messages[index];

                    //                     // If there's only 1 system message.
                    //                 } else {
                    //                     options.systemMessage = messages[0];
                    //                 }

                    //                 // Pass the system message transition if large_bttm.
                    //                 options.systemMessageTransition =
                    //                     options.systemMessage.template ===
                    //                     'large_bttm'
                    //                         ? 'component-slide-fade-up'
                    //                         : 'component-slide-down';
                    //             }
                    //         })

                    //         .catch(() => {
                    //             // Handle errors from request.
                    //         });
                    // }

                    if (typeof options.locale === 'undefined') {
                        options.locale = browserLocale;
                    } else {
                        options.locale = options.locale.substring(0, 2);
                    }

                    // Initialize i18n with browser language and default english as fallback.
                    // NOTE: i18n messages needs to be called messages or it won't work.
                    let i18n = new VueI18n({
                        locale:
                            options.locale === 'zh' ? 'zh-CN' : options.locale,
                        fallbackLocale: 'en',
                        messages
                    });

                    // Initialize for IE svg icons.
                    svg4everybody();

                    // Render Vue app with specified component
                    return new Vue({
                        render: h =>
                            h(pageConfig.default || pageConfig, {
                                props: options
                            }),
                        i18n
                    }).$mount(selector);
                }
            )
        )

        // If the requests failed. How to handle: Unknown.
        .catch(() => {
            // This is likely occurring due to an internet outage
            // OR a change to the s3 file location.
        });
};

export const createPage = (selector, page, options) => {
    requiredPages.keys().forEach(fileName => {
        // Get component config
        const pageConfig = requiredPages(fileName);

        // Set options to a json object if it's null to start.
        options = options ? options : {};

        // Check fileName against passed in component name
        if (fileName === `./${page}/${page}.vue`) {
            initApp(selector, pageConfig, options);
        }
    });
};

export const createComponent = (selector, component, options) => {
    requiredComponents.keys().forEach(fileName => {
        // Get component config
        const componentConfig = requiredComponents(fileName);

        // Set options to a json object if it's null to start.
        options = options ? options : {};

        // Check component name against passed in component name
        if (fileName === `./${component}/${component}.vue`) {
            initApp(selector, componentConfig, options);
        }
    });
};
